<template>
  <v-card-text>
    <!-- Options -->
    <v-row mt-6 mb-4>
      <v-col cols="12" sm="6" md="4" lg="3" xl="2">
        <v-select
          v-model="criteria.country"
          :items="countries"
          variant="outlined"
          item-title="text"
          label="Country"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <v-text-field
          v-model="criteria.fund_manager"
          type="text"
          variant="outlined"
          placeholder=" "
          label="Fund Manager"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <v-text-field
          v-model="criteria.security_name"
          type="text"
          variant="outlined"
          placeholder=" "
          label="Security Name"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="6" lg="4">
        <v-text-field
          v-model="criteria.isin"
          type="text"
          variant="outlined"
          placeholder=" "
          label="ISIN"
          hide-details
        />
      </v-col>
      <v-col md="6" lg="4" cols="12">
        <v-menu :close-on-content-click="false" transition="scale-transition" min-width="290px">
          <template #activator="{ props }">
            <v-text-field
              v-model="criteria.date_from"
              label="Position date from"
              :prepend-icon="mdiCalendar"
              hide-details
              readonly
              v-bind="props"
            />
          </template>
          <v-date-picker v-model="criteria.date_from" no-title scrollable />
        </v-menu>
      </v-col>

      <v-col cols="12" md="6" lg="4">
        <v-menu :close-on-content-click="false" transition="scale-transition" min-width="290px">
          <template #activator="{ props }">
            <v-text-field
              v-model="criteria.date_to"
              label="To"
              :prepend-icon="mdiCalendar"
              hide-details
              readonly
              v-bind="props"
            />
          </template>
          <v-date-picker v-model="criteria.date_to" no-title scrollable />
        </v-menu>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-data-table-server
      v-model:options="pagination"
      v-model:sort-by="pagination.sortBy"
      :headers="tableHeaders"
      :items="tableItems"
      :items-length="totalItems"
      :items-per-page-options="[10, 25, 50, 100]"
      :loading="loading"
      :no-data-text="no_data_text"
      must-sort
    >
      <template #item="{ item }">
        <tr>
          <td>
            <router-link :to="'/short/short_position_holder/' + item.filer_permalink">
              {{ item.position_holder_name }}
            </router-link>
          </td>
          <td>{{ item.issuer_name }}</td>
          <td>{{ rounded_number(item.net_short_position) }}</td>
          <td>{{ item.position_date }}</td>
          <td>{{ item.country }}</td>
          <td><span v-if="item.is_current">Yes</span><span v-else>No</span></td>
        </tr>
      </template>
    </v-data-table-server>
  </v-card-text>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { apiRoot } from '@/store/api'
import debounce from 'lodash/debounce'

// Mixins
import FormattersMixin from '@/mixins/formatters'
import { mdiCalendar } from '@mdi/js'

export default {
  components: {},
  mixins: [FormattersMixin],

  data() {
    return {
      mdiCalendar,
      loading: false,
      tableHeaders: [
        { title: 'Holder', key: 'issuer_name' },
        { title: 'Issuer', key: 'position_holder_name' },
        { title: 'Net Short Position', key: 'net_short_position' },
        { title: 'Position Date', key: 'position_date' },
        { title: 'Origin', key: 'country' },
        { title: 'Is Current', key: 'is_current' }
      ],
      tableItems: [],
      totalItems: 0,
      pagination: {
        sortBy: [{ key: 'position_date', order: 'desc' }],
        page: 1,
        itemsPerPage: 10
      },
      criteria: {
        country: 'all',
        security_name: '',
        fund_manager: '',
        isin: '',
        date_from: null,
        date_to: null,
        is_current: false
      },
      countries: [
        { value: 'all', text: 'All' },
        { value: 'austria', text: 'Austria' },
        { value: 'belgium', text: 'Belgium' },
        { value: 'denmark', text: 'Denmark' },
        { value: 'finland', text: 'Finland' },
        { value: 'france', text: 'France' },
        { value: 'germany', text: 'Germany' },
        { value: 'greece', text: 'Greece' },
        { value: 'hong kong', text: 'Hong Kong' },
        { value: 'iceland', text: 'Iceland' },
        { value: 'ireland', text: 'Ireland' },
        { value: 'italy', text: 'Italy' },
        { value: 'japan', text: 'Japan' },
        { value: 'netherlands', text: 'Netherlands' },
        { value: 'norway', text: 'Norway' },
        { value: 'poland', text: 'Poland' },
        { value: 'portugal', text: 'Portugal' },
        { value: 'spain', text: 'Spain' },
        { value: 'sweden', text: 'Sweden' }
      ],
      results: null
    }
  },

  computed: {
    params() {
      return {
        aggregate_country: this.criteria.aggregate_country,
        aggregate_security_name: this.criteria.aggregate_security_name,
        rows: this.pagination.itemsPerPage,
        page: this.pagination.page,
        sort: this.pagination.sortBy[0]?.key,
        order: this.pagination.sortBy[0]?.order || 'asc',
        ...this.filtersParamsObject
      }
    },
    isLoggedIn() {
      return useUserStore().isLoggedIn
    },
    no_data_text() {
      if (!this.isLoggedIn) {
        return 'You must be logged in to use this search.'
      } else if (this.loading) {
        return 'Loading...'
      } else if (this.results == 'subscription required') {
        return 'Subscription required'
      } else {
        return 'No results found.'
      }
    }
  },

  watch: {
    params: {
      deep: true,
      handler() {
        this.updateData()
      }
    },
    isLoggedIn(val) {
      if (val) this.updateData()
    }
  },

  methods: {
    updateData: debounce(function () {
      if (!useUserStore().isLoggedIn) {
        this.tableItems = []
        this.totalItems = 0
        this.loading = false
        return
      }
      this.loading = true

      apiRoot
        .get('/short_position/search_table?', { params: this.params })
        .then(response => {
          this.results = response.data
          if (response.data.rows) {
            this.tableItems = response.data.rows
            this.totalItems = response.data.records
          }
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .then(() => (this.loading = false))
    }, 750)
  }
}
</script>
